type ValidateUrlResponse = {
  url: string;
  isValid: boolean;
};

/**
 *  validateUrl - validates the url
 * @param redirectUrl - the redirect url to validate
 * @returns ValidateUrlResponse
 */
export const validateUrl = (redirectUrl: string | null | undefined): ValidateUrlResponse => {
  if (!redirectUrl) {
    return { url: '', isValid: false };
  }

  try {
    const parsedRedirectUrl = new URL(redirectUrl);
    const queryParams = new URLSearchParams(parsedRedirectUrl.search);

    // check if the redirect url protocol is https
    if (parsedRedirectUrl.protocol !== 'https:') {
      return { url: '', isValid: false };
    }

    // check if the redirect url hostname is cloud.digitalocean.com
    if (!parsedRedirectUrl.hostname.endsWith('digitalocean.com')) {
      return { url: '', isValid: false };
    }

    // check if the redirect url pathname is /apps/new
    if (parsedRedirectUrl.pathname !== '/apps/new') {
      return { url: '', isValid: false };
    }

    try {
      // check if the redirect url has a template query param or a repo query param
      const hasTemplateUrl = parsedRedirectUrl.searchParams.has('template');
      const hasRepoUrl = parsedRedirectUrl.searchParams.has('repo');
      if (!hasTemplateUrl && !hasRepoUrl) {
        return { url: '', isValid: false };
      }

      const templateUrl = hasTemplateUrl ? new URL(parsedRedirectUrl.searchParams.get('template') as string) : null;
      const repoUrl = hasRepoUrl ? new URL(parsedRedirectUrl.searchParams.get('repo') as string) : null;

      // check if the repo url hostname or the template url hostname is github.com
      if (repoUrl?.hostname !== 'github.com' && templateUrl?.hostname !== 'github.com') {
        return { url: '', isValid: false };
      }

      for (const [key, value] of Array.from(queryParams.entries())) {
        // delete all query params that do not belong to the template or repo query params
        if (key !== 'template' && key !== 'repo') {
          queryParams.delete(key);
          continue;
        }
        // check if the search params value has any embedded JavaScript-related suspicious patterns
        if (containsScriptInjection(value)) {
          return { url: '', isValid: false };
        }

        // remove any extra query params that are appended to the template or repo query params
        const filteredValue = filterExtraQueryParams(value);
        if (value !== filteredValue) {
          queryParams.set(key, filteredValue);
        }
      }

      const regenerateUrl = new URL(
        `${parsedRedirectUrl.origin}${parsedRedirectUrl.pathname}?${decodeURIComponent(queryParams.toString())}`,
      );

      return { url: regenerateUrl.toString(), isValid: true };
    } catch (e) {
      return { url: '', isValid: false };
    }
  } catch (e) {
    return { url: '', isValid: false };
  }
};

const filterExtraQueryParams = (queryParamValue: string) => {
  try {
    const parsedUrl = new URL(queryParamValue);
    const extraSearchParams = new URLSearchParams(parsedUrl.search);
    const hasQueryParams = Array.from(extraSearchParams.keys()).length > 0;
    if (hasQueryParams) {
      return `${parsedUrl.origin}${parsedUrl.pathname}`;
    }
    return queryParamValue;
  } catch (e) {
    return queryParamValue;
  }
};

const containsScriptInjection = (str: string) => {
  const maliciousJavascriptPatterns = [
    /javascript:/i,
    /data:/i,
    /<script/i,
    /on\w+\s*=/i,
    /alert\s*\(/i,
    /eval\s*\(/i,
    /Function\s*\(/i,
    /setTimeout\s*\(/i,
    /setInterval\s*\(/i,
    /new\s+Function/i,
  ];
  return maliciousJavascriptPatterns.some((pattern) => pattern.test(str));
};
